"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, CircleAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function NewsGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border-border/50 shadow-sm">
            <AspectRatio ratio={16 / 9} className="bg-muted/50 animate-pulse" />
            <CardHeader className="space-y-2">
              <div className="h-6 w-3/4 rounded-md bg-muted/50 animate-pulse" />
              <div className="h-6 w-1/2 rounded-md bg-muted/50 animate-pulse" />
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="h-4 w-full rounded-md bg-muted/50 animate-pulse" />
                <div className="h-4 w-full rounded-md bg-muted/50 animate-pulse" />
                <div className="h-4 w-2/3 rounded-md bg-muted/50 animate-pulse" />
              </div>
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-destructive/20 bg-destructive/5 p-8 text-center">
        <CircleAlert className="mb-4 size-8 text-destructive" />
        <p className="mb-4 text-sm text-muted-foreground">
          Failed to load the latest news. Please try again later.
        </p>
        <Button variant="outline" onClick={refetch}>
          Retry
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-border/50 bg-muted/20 p-12 text-center">
        <p className="text-muted-foreground">No news updates available at this time.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => {
      const cleanExcerpt = post.excerpt?.replace(/(<([^>]+)>)/gi, "") || "";
      const displayDate = post.date ? new Date(post.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      }) : "Recent";
      return <Card key={post.id} data-index={i} className="group flex h-full flex-col overflow-hidden border-border/50 bg-card shadow-sm transition-all duration-300 hover:shadow-md">
            <div className="relative overflow-hidden">
              <AspectRatio ratio={16 / 9}>
                <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1590036/img/generic_news_placeholder.png"} alt={post.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </AspectRatio>
              <div className="absolute left-4 top-4">
                <Badge className="bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/90">
                  <SafeHydrate><Calendar className="mr-1.5 size-3" /></SafeHydrate>
                  {displayDate}
                </Badge>
              </div>
            </div>

            <CardHeader className="flex-none pb-4">
              <CardTitle className="font-serif text-xl leading-tight text-primary line-clamp-2">
                {post.title}
              </CardTitle>
            </CardHeader>

            <CardContent className="flex-grow pb-6">
              <CardDescription className="text-base text-muted-foreground line-clamp-3">
                {cleanExcerpt}
              </CardDescription>
            </CardContent>

            <CardFooter className="mt-auto pt-0">
              <Link to={post.link || "#"} className="group/link inline-flex items-center text-sm font-semibold text-primary transition-colors hover:text-secondary">
                Read More
                <ArrowRight className="ml-1.5 size-4 text-secondary opacity-0 transition-all duration-300 -translate-x-2 group-hover/link:translate-x-0 group-hover/link:opacity-100" />
              </Link>
            </CardFooter>
          </Card>;
    })}
    </div>;
}
export default function FoundationNewsUpdates() {
  const wp_query_params = {
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return;
}